import { Draggable } from "react-beautiful-dnd";
import { DashboardPin, DashboardPinRow } from "../../../models/pageModels/ReportDashboard";
import getclassNames from "../../../utils/getClassNames";
import PinCardActionMenu from "./PinCardActionMenu";

interface DashboardPinCardProps {
    pin: DashboardPin;
    index: number;
    isReorderMode: boolean;
    isRefreshing?: boolean;
    isFirst?: boolean;
    isLast?: boolean;
    onMoveToTop?: () => void;
    onMoveUp?: () => void;
    onMoveDown?: () => void;
    onMoveToBottom?: () => void;
}

const DashboardPinCard = ({
    pin,
    index,
    isReorderMode,
    isRefreshing = false,
    isFirst = false,
    isLast = false,
    onMoveToTop,
    onMoveUp,
    onMoveDown,
    onMoveToBottom,
}: DashboardPinCardProps) => {

    const isSingleValue = (rows: DashboardPinRow[]) => {
        return rows.length === 1 &&
            (rows[0].fieldGrouping === null || rows[0].fieldGrouping === "");
    };

    const renderValue = () => {
        if (pin.rows.length === 0) {
            return <span className="pin-no-data">No data available</span>;
        }

        // Single value card (e.g. Total Participants)
        if (isSingleValue(pin.rows)) {
            const row = pin.rows[0];
            return (
                <div className="single-value-container">
                    <span className="single-value-label">{row.fieldLabel}</span>
                    <div className="single-value-number">{row.totalValue ?? row.averageValue ?? row.medianValue ?? "N/A"}</div>
                </div>
            );
        }

        // Table card (e.g. Gender, Ethnicity, Age)
        const hasPercentage = pin.rows.some(r => r.percentageValue !== null);
        const hasTotal = pin.rows.some(r => r.totalValue !== null);
        const rowTotal = pin.rows.reduce((sum, r) => sum + (r.totalValue ?? 0), 0);

        return (
            <table className="pin-stats-table">
                <thead>
                    <tr>
                        <th className="stat-label">value</th>
                        {hasTotal && <th className="stat-value">total</th>}
                        {hasPercentage && <th className="stat-value">percentage</th>}
                    </tr>
                </thead>
                <tbody>
                    {pin.rows.map(row => (
                        <tr key={row.summaryRowId}>
                            <td className="stat-label">{row.fieldGrouping || "N/A"}</td>
                            {hasTotal && <td className="stat-value">{row.totalValue ?? "N/A"}</td>}
                            {hasPercentage && <td className="stat-value">{row.percentageValue !== null ? `${row.percentageValue.toFixed(2)}%` : "N/A"}</td>}
                        </tr>
                    ))}
                    {hasTotal && (
                        <tr className="stat-total-row">
                            <td className="stat-label"></td>
                            <td className="stat-value">{rowTotal}</td>
                            {hasPercentage && <td></td>}
                        </tr>
                    )}
                </tbody>
            </table>
        );
    };

    const renderOutdatedStatus = () => {
        if (!pin.isOutdated) return null;

        return (
            <span className="text-danger small pin-status">
                <i className="ri-error-warning-line margin-end-1"></i>
                {pin.reportLastRun
                    ? <>Outdated &mdash; {new Date(pin.reportLastRun).toLocaleDateString()}</>
                    : "Never run"
                }
            </span>
        );
    };

    const renderReorderControls = () => (
        <div className="pin-reorder-controls" aria-label={`Move ${pin.displayName}`}>
            <button
                type="button"
                className="pin-reorder-button"
                onClick={onMoveToTop}
                disabled={isFirst}
                title="Move to top"
                aria-label={`Move ${pin.displayName} to top`}
            >
                <i className="ri-align-top"></i>
            </button>
            <button
                type="button"
                className="pin-reorder-button"
                onClick={onMoveUp}
                disabled={isFirst}
                title="Move up"
                aria-label={`Move ${pin.displayName} up`}
            >
                <i className="ri-arrow-up-s-line"></i>
            </button>
            <button
                type="button"
                className="pin-reorder-button"
                onClick={onMoveDown}
                disabled={isLast}
                title="Move down"
                aria-label={`Move ${pin.displayName} down`}
            >
                <i className="ri-arrow-down-s-line"></i>
            </button>
            <button
                type="button"
                className="pin-reorder-button"
                onClick={onMoveToBottom}
                disabled={isLast}
                title="Move to bottom"
                aria-label={`Move ${pin.displayName} to bottom`}
            >
                <i className="ri-align-bottom"></i>
            </button>
        </div>
    );

    return (
        <Draggable draggableId={pin.pinId} index={index} isDragDisabled={!isReorderMode}>
            {(provided, snapshot) => (
                <div
                    className={isReorderMode ? "dashboard-pin-reorder-item-wrapper" : "column-small-12 column-medium-6 column-large-4 margin-bottom-4"}
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                >
                    <div className={getclassNames(
                        "dashboard-pin-card",
                        isReorderMode ? "reorder-mode" : "",
                        isReorderMode ? "dashboard-pin-reorder-card" : "",
                        snapshot.isDragging ? "dragging" : ""
                    )}>
                        {isReorderMode ? (
                            <div className="pin-header pin-reorder-header">
                                <div className="pin-reorder-position" aria-label={`Position ${index + 1}`}>
                                    {index + 1}
                                </div>
                                <div className="pin-reorder-drag-handle" {...provided.dragHandleProps}>
                                    <i className="ri-drag-move-fill drag-handle-icon"></i>
                                    <div className="pin-title-block">
                                        <h4 className="text-truncate pin-title" title={pin.displayName}>{pin.displayName}</h4>
                                        <span className="pin-subtitle text-truncate" title={pin.reportName}>{pin.reportName}</span>
                                        {renderOutdatedStatus()}
                                    </div>
                                </div>
                                {renderReorderControls()}
                            </div>
                        ) : (
                            <>
                                <div className="pin-header row justify-content-between align-items-start no-wrap">
                                    <div className="column min-width-0">
                                        <h4 className="text-truncate pin-title" title={pin.displayName}>{pin.displayName}</h4>
                                        <span className="pin-subtitle text-truncate" title={pin.reportName}>{pin.reportName}</span>
                                        {renderOutdatedStatus()}
                                    </div>
                                    <div className="pin-actions">
                                        <PinCardActionMenu
                                            pinId={pin.pinId}
                                            displayName={pin.displayName}
                                            reportId={pin.reportId}
                                            disabled={isRefreshing}
                                        />
                                    </div>
                                </div>

                                <div className="pin-content">
                                    {renderValue()}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </Draggable>
    );
};

export default DashboardPinCard;
